import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft, MapPin, Gift, CreditCard, Wallet, Heart, Share2, HelpCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";

const Help = () => {
  const navigate = useNavigate();

  const sendingSteps = [
    {
      icon: MapPin,
      title: "Enable Location Sharing",
      description: "Turn on location sharing to see nearby users on the map. Choose 'Precise' for exact location or 'Coarse' for approximate."
    },
    {
      icon: Gift,
      title: "Find Someone to Gift",
      description: "Browse the map or user list to find someone nearby. You can also search for users by name or username."
    },
    {
      icon: CreditCard,
      title: "Send Your Gift",
      description: "Tap 'Send a Gift', choose an amount (up to £50), add an optional message, and complete payment securely via Stripe."
    },
    {
      icon: Share2,
      title: "Share the Kindness",
      description: "After sending, share your act of kindness on social media to inspire others!"
    }
  ];

  const receivingSteps = [
    {
      icon: MapPin,
      title: "Share Your Location",
      description: "Enable location sharing so others can find you on the map. You control your visibility with privacy settings."
    },
    {
      icon: Wallet,
      title: "Set Up Payouts",
      description: "Go to Settings and complete Stripe onboarding to receive gifts directly to your bank account."
    },
    {
      icon: Gift,
      title: "Receive Gifts",
      description: "When someone sends you a gift, you'll receive a notification. The money goes straight to your connected account."
    },
    {
      icon: Heart,
      title: "Spread Gratitude",
      description: "Thank your gifter (if not anonymous) and consider paying it forward by gifting someone else!"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-warm">
      <div className="container mx-auto px-4 pt-safe pb-6 max-w-md md:max-w-2xl lg:max-w-4xl">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="h-10 w-10"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-xl font-bold bg-gradient-sunset bg-clip-text text-transparent">
            How It Works
          </h1>
        </div>

        {/* Sending Gifts Section */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Gift className="w-5 h-5 text-primary" />
            Sending Gifts
          </h2>
          <div className="space-y-4">
            {sendingSteps.map((step, index) => (
              <Card key={index} className="p-4 shadow-soft border-border/50">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <step.icon className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-medium text-muted-foreground">Step {index + 1}</span>
                    </div>
                    <h3 className="font-medium mb-1">{step.title}</h3>
                    <p className="text-sm text-muted-foreground">{step.description}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Receiving Gifts Section */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Wallet className="w-5 h-5 text-accent" />
            Receiving Gifts
          </h2>
          <div className="space-y-4">
            {receivingSteps.map((step, index) => (
              <Card key={index} className="p-4 shadow-soft border-border/50">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                    <step.icon className="w-5 h-5 text-accent" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-medium text-muted-foreground">Step {index + 1}</span>
                    </div>
                    <h3 className="font-medium mb-1">{step.title}</h3>
                    <p className="text-sm text-muted-foreground">{step.description}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Tips Section */}
        <Card className="p-4 shadow-warm border-border/50 bg-primary/5">
          <h3 className="font-semibold mb-2">💡 Quick Tips</h3>
          <ul className="text-sm text-muted-foreground space-y-2">
            <li>• You can send up to £50 per gift</li>
            <li>• A small processing fee helps keep the app running</li>
            <li>• Save users you frequently gift for quick access</li>
            <li>• Check your Dashboard to track sent and received gifts</li>
          </ul>
        </Card>

        {/* CTA */}
        <div className="mt-6 space-y-3">
          <Button 
            className="w-full bg-primary hover:bg-primary/90"
            onClick={() => navigate("/map")}
          >
            <MapPin className="w-4 h-4 mr-2" />
            Start Exploring the Map
          </Button>
          <Button 
            variant="outline"
            className="w-full"
            onClick={() => navigate("/support")}
          >
            <HelpCircle className="w-4 h-4 mr-2" />
            Need Help? Contact Support
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Help;
